import { useEffect } from 'react'

import Cookie from '@design-system/cookie'

import { useLocale } from 'context/LocaleContext'
import { useCookieConsentPropsStore } from 'store/useCookieConsentProps'
import { useCookieConsentStore } from 'store/useCookieConsentStore'

import { useCookieConsent } from 'hooks/cookieConsent/useCookieConsent'

/**
 * Fallback texts per brand, used when the CMS article is not placed on the
 * page (or its texts are empty). Same pattern as ModalAbandonedCart.
 * TODO(TCS-8180): legal copy pending approval (LGPD/ANPD) — replace once
 * Legal/Privacy signs off.
 */
const COOKIE_CONSENT_FALLBACK_PROPS = {
  br: {
    TITLE: 'Este site usa cookies',
    DESCRIPTION:
      'A Sky+ utiliza cookies para melhorar sua experiência em nosso website. você pode personalizar suas preferências, aceitando ou rejeitando todos os cookies opcionais ou escolhendo quais permitir. Saiba mais em nossa <a href="/cookies">Política de Cookies</a>.',
    ACCEPT_ALL: 'Aceitar todos',
    REFUSE_NON_ESSENTIAL: 'Recusar opcionais',
    MANAGE: 'Configurar'
  },
  ssla: {
    TITLE: 'Este sitio usa cookies',
    DESCRIPTION:
      'DGO utiliza cookies para mejorar tu experiencia en nuestro sitio web. Puedes personalizar tus preferencias, aceptando o rechazando todas las cookies opcionales o eligiendo cuáles permitir. Obtén más información en nuestra <a href={/cookies}>Política de Cookies</a>.',
    ACCEPT_ALL: 'Aceptar todas',
    REFUSE_NON_ESSENTIAL: 'Rechazar opcionales',
    MANAGE: 'Configurar'
  }
}

export default function CookieConsentBanner() {
  const { brand } = useLocale()

  /** CMS kill-switch: when the article sets it to false, nothing renders */
  const showCookieConsent = useCookieConsentPropsStore.use.showCookieConsent()

  const isBannerVisible = useCookieConsentStore.use.isBannerVisible()
  const showBanner = useCookieConsentStore.use.showBanner()
  const openPreferences = useCookieConsentStore.use.openPreferences()
  const hasOpenedPreferences = useCookieConsentStore.use.hasOpenedPreferences()

  const { hasConsent, handleAcceptAll, handleRefuseNonEssential } =
    useCookieConsent()

  const lang = brand === 'sky-mais' ? 'br' : 'ssla'
  const fallback = COOKIE_CONSENT_FALLBACK_PROPS[lang]

  /** CMS texts (Liferay article) with hardcoded fallbacks per brand */
  const { bannerConsentTexts } = useCookieConsentPropsStore()
  const title = bannerConsentTexts.bannerTitle || fallback.TITLE
  const description =
    bannerConsentTexts.bannerDescription || fallback.DESCRIPTION
  const acceptAllLabel =
    bannerConsentTexts.bannerAcceptAllLabel || fallback.ACCEPT_ALL
  const refuseLabel =
    bannerConsentTexts.bannerRefuseNonEssentialLabel ||
    fallback.REFUSE_NON_ESSENTIAL
  const manageLabel = bannerConsentTexts.bannerManageLabel || fallback.MANAGE

  /**
   * Show the banner only after mount (when we know whether a valid consent
   * exists) and only while the user has not saved a decision. The store
   * starts with isBannerVisible = false to avoid hydration mismatches.
   * Once the preferences center has been opened, the banner stays dismissed
   * for the session: after the modal closes, only the floating button
   * remains as entry point.
   */
  useEffect(() => {
    if (!showCookieConsent) return
    if (!hasConsent && !hasOpenedPreferences) {
      showBanner()
    }
  }, [showCookieConsent, hasConsent, hasOpenedPreferences, showBanner])

  if (!showCookieConsent || !isBannerVisible) {
    return null
  }

  return (
    <Cookie
      title={title}
      description={description}
      buttonPrimary={{
        children: acceptAllLabel,
        onClick: handleAcceptAll,
        'aria-label': acceptAllLabel
      }}
      buttonSecondary={{
        children: refuseLabel,
        onClick: handleRefuseNonEssential,
        'aria-label': refuseLabel
      }}
      buttonTertiary={{
        children: manageLabel,
        onClick: openPreferences,
        'aria-label': manageLabel
      }}
    />
  )
}
